import { createContext, useContext, useEffect, useCallback, useMemo } from "react";
import { useGetUnreadCount } from "../hooks/organization/notification/useGetUnreadCount";

const NotificationContext = createContext(undefined);

export const NotificationProvider = ({ children }) => {
    const { unreadCount = 0, loading, error, getUnreadCount, setUnreadCount } = useGetUnreadCount();

    /** Fetch count from server */
    const fetchUnreadCount = useCallback(async () => {
        await getUnreadCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Optimistic local decrement (UI only) */
    const decrementUnreadCount = useCallback(() => {
        setUnreadCount((prev) => (prev && prev > 0 ? prev - 1 : 0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Fetch once on mount */
    useEffect(() => {
        fetchUnreadCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Memoize context value to prevent unnecessary re-renders */
    const value = useMemo(() => ({
        notificationCount: unreadCount,
        loading,
        error,
        fetchUnreadCount,
        decrementUnreadCount,
    }), [unreadCount, loading, error, fetchUnreadCount, decrementUnreadCount]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error("useNotification must be used within NotificationProvider");
    }
    return context;
};